import { Box, Chip, Divider, Stack, Typography, alpha, useTheme } from "@mui/material";
import type { ReactNode } from "react";

import {
	searchPaletteCountChipSx,
	searchPalettePreviewCardSx,
	searchPalettePreviewPanelSx,
} from "./searchPaletteStyles";

type SearchPaletteSectionHeaderProps = {
	label: string;
	count: number;
	accentColor: string;
};

export function SearchPaletteSectionHeader({
	label,
	count,
	accentColor,
}: SearchPaletteSectionHeaderProps) {
	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			sx={{ px: 1, pt: 1, pb: 0.75 }}
		>
			<Typography
				variant="caption"
				sx={{
					fontWeight: 900,
					letterSpacing: 0.4,
					textTransform: "uppercase",
					color: accentColor,
				}}
			>
				{label}
			</Typography>
			<Chip size="small" label={count} variant="outlined" sx={searchPaletteCountChipSx(accentColor)} />
		</Stack>
	);
}

type SearchPaletteAttributeItem = {
	label: string;
	value?: ReactNode;
};

type SearchPaletteAttributeListProps = {
	items: SearchPaletteAttributeItem[];
};

export function SearchPaletteAttributeList({
	items,
}: SearchPaletteAttributeListProps) {
	const filteredItems = items.filter(
		(item) => item.value !== undefined && item.value !== null && item.value !== "",
	);

	if (filteredItems.length === 0) return null;

	return (
		<Stack spacing={0.9}>
			{filteredItems.map((item, index) => (
				<Stack
					key={item.label}
					direction="row"
					spacing={1.25}
					alignItems="flex-start"
					justifyContent="space-between"
					sx={{
						...(index > 0
							? {
									pt: 0.9,
									borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.45)}`,
								}
							: {}),
					}}
				>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{
							flex: "0 0 84px",
							fontWeight: 800,
							letterSpacing: 0.2,
							pt: 0.15,
						}}
					>
						{item.label}
					</Typography>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						{typeof item.value === "string" || typeof item.value === "number" ? (
							<Typography variant="body2" sx={{ lineHeight: 1.45 }}>
								{item.value}
							</Typography>
						) : (
							item.value
						)}
					</Box>
				</Stack>
			))}
		</Stack>
	);
}

type SearchPaletteQuickLookPanelProps = {
	accentColor: string;
	label: string;
	title?: string;
	subtitle?: string;
	body?: ReactNode;
	bodyLabel?: string;
	meta?: ReactNode;
	metaLabel?: string;
	actions?: ReactNode;
	emptyText?: string;
};

export function SearchPaletteQuickLookPanel({
	accentColor,
	label,
	title,
	subtitle,
	body,
	bodyLabel = "Summary",
	meta,
	metaLabel = "Attributes",
	actions,
	emptyText = "No selection",
}: SearchPaletteQuickLookPanelProps) {
	const theme = useTheme();

	return (
		<Box sx={searchPalettePreviewPanelSx(theme)}>
			{title ? (
				<>
					<Box
						sx={{
							...searchPalettePreviewCardSx(theme),
							p: 1.35,
						}}
					>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Chip
								size="small"
								label={label}
								variant="outlined"
								sx={{
									height: 24,
									borderRadius: 999,
									fontWeight: 900,
									bgcolor: alpha(accentColor, 0.08),
									borderColor: alpha(accentColor, 0.24),
									color: accentColor,
								}}
							/>
						</Stack>

						<Typography
							sx={{
								fontWeight: 950,
								mt: 0.9,
								fontSize: { xs: 19, sm: 21 },
								lineHeight: 1.22,
								letterSpacing: -0.2,
							}}
						>
							{title}
						</Typography>

						{subtitle ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ display: "block", mt: 0.65, lineHeight: 1.5 }}
							>
								{subtitle}
							</Typography>
						) : null}

						{actions ? (
							<Box sx={{ mt: 1 }}>
								<Stack
									direction="row"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{
										"& .MuiButton-root": {
											minHeight: 32,
											px: 1.1,
											borderRadius: 1.5,
											textTransform: "none",
											fontWeight: 800,
											fontSize: 12,
											lineHeight: 1.1,
											boxShadow: "none",
										},
										"& .MuiButton-startIcon": {
											mr: 0.6,
											"& > *:nth-of-type(1)": {
												fontSize: 16,
											},
										},
									}}
								>
									{actions}
								</Stack>
							</Box>
						) : null}
					</Box>

					{body || meta ? (
						<Box sx={{ ...searchPalettePreviewCardSx(theme), p: 1.2 }}>
							{body ? (
								<>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									mb: 0.65,
									fontWeight: 900,
									letterSpacing: 0.4,
									textTransform: "uppercase",
									color: "text.secondary",
								}}
							>
								{bodyLabel}
							</Typography>
							{body}
								</>
							) : null}

							{body && meta ? <Divider sx={{ my: 1.1 }} /> : null}

							{meta ? (
								<>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									mb: 0.65,
									fontWeight: 900,
									letterSpacing: 0.4,
									textTransform: "uppercase",
									color: "text.secondary",
								}}
							>
								{metaLabel}
							</Typography>
							{meta}
								</>
							) : null}
						</Box>
					) : null}
				</>
			) : (
				<Box
					sx={{
						...searchPalettePreviewCardSx(theme),
						p: 1.6,
						minHeight: 220,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						textAlign: "center",
					}}
				>
					<Box>
						<Box
							sx={{
								width: 10,
								height: 10,
								borderRadius: "50%",
								bgcolor: alpha(accentColor, 0.7),
								mx: "auto",
								boxShadow: `0 0 0 8px ${alpha(accentColor, 0.08)}`,
							}}
						/>
						<Typography sx={{ mt: 1.75, fontWeight: 800 }}>{emptyText}</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
							Select a result to inspect it here.
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
}
